//Copyright (C) Siemens AG 2026. All rights reserved. Confidential
import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { FailAvailableNeoSimitVersions, IdentityFailure, IdentitySuccess, LoadPortalAuthUser,LoadIdentity, AuthPortalUserFailure, LoadAvailableNeoSimitVersions, SuccessAvailableNeoSimitVersions, AuthPortalUserSuccess, TenantIdChanged, LoadCurrentTenantProjects, RefreshCurrentTenantProjects, SuccessCurrentTenantProjects, FailureCurrentTenantProjects, LoadCurrentTenantUsers, FailureCurrentTenantUsers, SuccessCurrentTenantUsers, SuccessCurrentTenantSubTenants, LoadCurrentTenantSubTenants, SuccessCurrentTenantPricingPlans, FailureCurrentTenantPricingPlans, LoadCurrentTenantPricingPlans, LoadAnnouncements, SuccessAnnouncements, FailureAnnouncements, SuccessSingleAnnouncementImage, RefreshPortalAuthUser, FailureSingleAnnouncementImage, FailureCurrentTenantSubTenants, LoadCurrentTenantPriceList, SuccessCurrentTenantPriceList, FailureCurrentTenantPriceList } from "./actions/app.actions";
import { Observable, catchError, filter, from, interval, map, mergeMap, of, pairwise, startWith, switchMap, takeUntil, withLatestFrom } from "rxjs";
import { TenantProjectService } from "../services/tenant/tenant-project.service";
import { TenantUserService } from "../services/tenant/tenant-user.service";
import { SubTenantService } from "../services/tenant/sub-tenant.service";
import { UserManagementService } from "../services/user/user.management.service";
import { Store, select } from "@ngrx/store";
import { authPortalUserState, selectAvailableNeoSimitVersionsState, identityState, announcementState, currentTenantDataState } from "./app.state";
import { ParamterStoreService } from "../services/utility/parameter.store";
import { AuthService } from "../services/auth/auth.service";
import { PricingplanService } from "../services/pricingplan.service";
import { PriceListService } from "../services/tenant/price-list.service";

const STATUS_NOT_LOADED = 'not-loaded';
const AUTH_USER_REFRESH_INTERVAL_MS = 300000;

@Injectable()
export class AppEffects {

    identityToastClose: any = null;

    initializeCoreData$ = createEffect(() => this.actions.pipe(
        ofType(LoadIdentity),
        withLatestFrom(this.store.select(identityState)),
        filter(([actions, identityState]) => identityState.status === 'not-checked'),
        mergeMap(([actions, identityState]) => {

            return new Observable<any>(sub => {
                this.authService.auth0?.validateToken(identityState.idToken, '', (error, result) => {

                    if (error !== null) {

                        this.authService.logout()
                        sub.next(IdentityFailure({ payload: undefined }))
                        //sub.complete()
                    } else {

                        sub.next(IdentitySuccess({ payload: result }))
                        //sub.complete()
                    }

                    sub.complete()
                })

            })
        })
    ))

    successIdentityState$ = createEffect(() => this.actions.pipe(
        ofType(IdentitySuccess),
        mergeMap(() => of(LoadPortalAuthUser({ payload: undefined })))
    ))

    failIdentityState$ = createEffect(() => this.actions.pipe(
        ofType(IdentityFailure),
        mergeMap(() => {
            // this.authService.logout(true); // Assuming this handles the logout and redirection
            return of(AuthPortalUserFailure({ payload: undefined }));
        })
    ))

    loadPortalAuthUser$ = createEffect(() => this.actions.pipe(
        ofType(LoadPortalAuthUser),
        withLatestFrom(this.store.select(authPortalUserState)),
        filter(([actions, authUserState]) => authUserState.status === STATUS_NOT_LOADED),
        mergeMap(([actions, authUserState]) => {

            return this.usersManagementService.getMe().pipe(
                map(user => {

                    if (user.prefTenant != null && user.tenantProfiles.some(tp => tp.tenantId === user.prefTenant)) {

                        return AuthPortalUserSuccess({ payload: { authUser: user, currentTenantId: user.prefTenant } });
                    } else {
                        if (user.tenantProfiles.length) {

                            return AuthPortalUserSuccess({ payload: { authUser: user, currentTenantId: user.tenantProfiles[0].tenantId } });
                        } else {

                            return AuthPortalUserSuccess({ payload: { authUser: user, currentTenantId: null } });
                        }
                    }
                }),
                catchError(() => { return of(AuthPortalUserFailure({ payload: undefined })) })
            )

        })
    ));

    refreshPortalAuthUser$ = createEffect(() => this.actions.pipe(
        ofType(RefreshPortalAuthUser),
        mergeMap(() => {
            return this.usersManagementService.getMe().pipe(
                map(user => {
                    if (user.prefTenant != null && user.tenantProfiles.some(tp => tp.tenantId === user.prefTenant)) {
                        return AuthPortalUserSuccess({ payload: { authUser: user, currentTenantId: user.prefTenant } });
                    } else if (user.tenantProfiles.length) {
                        return AuthPortalUserSuccess({ payload: { authUser: user, currentTenantId: user.tenantProfiles[0].tenantId } });
                    } else {
                        return AuthPortalUserSuccess({ payload: { authUser: user, currentTenantId: null } });
                    }
                }),
                catchError(() => of(AuthPortalUserFailure({ payload: undefined })))
            );
        })
    ));

    autoRefreshPortalAuthUser$ = createEffect(() => this.actions.pipe(
        ofType(AuthPortalUserSuccess),
        switchMap(() => interval(AUTH_USER_REFRESH_INTERVAL_MS).pipe(
            map(() => RefreshPortalAuthUser({ payload: undefined })),
            takeUntil(this.actions.pipe(ofType(AuthPortalUserFailure)))
        ))
    ));

    detectTenantIdChange$ = createEffect(() => this.store.select(authPortalUserState).pipe(
        filter(state => state.status === 'loaded'),
        pairwise(),
        filter(([previousState, currentState]) => previousState.currentTenantId !== currentState.currentTenantId),
        map(([previousState, currentState]) => TenantIdChanged({
            payload: {
                previousTenantId: previousState.currentTenantId,
                newTenantId: currentState.currentTenantId,
            }
        }))
    ));

    authPortalUserSuccessProjects$ = createEffect(() => this.actions.pipe(
        ofType(AuthPortalUserSuccess, TenantIdChanged),
        withLatestFrom(
            this.store.pipe(select(authPortalUserState)),
            this.store.pipe(select(currentTenantDataState)),
            this.store.pipe(select(announcementState)),
            this.store.pipe(select(selectAvailableNeoSimitVersionsState))
        ),
        filter(([_, authState]) => authState.status === 'loaded' && authState.authUser !== null && authState.currentTenantId !== null),
        mergeMap(([_, authState, tenantData, announcements, versionsState]) => {
            const actions: any[] = [];

            if (tenantData.projectsStatus === STATUS_NOT_LOADED) {
                actions.push(LoadCurrentTenantProjects({ payload: undefined }));
            }
            if (tenantData.usersStatus === STATUS_NOT_LOADED) {
                actions.push(LoadCurrentTenantUsers({ payload: undefined }));
            }
            if (announcements.status === STATUS_NOT_LOADED) {
                actions.push(LoadAnnouncements({ payload: undefined }));
            }
            if (authState.currentTenantId && authState.authUser?.canSeeCostInformation(authState.currentTenantId)
                && tenantData.priceListStatus === STATUS_NOT_LOADED) {
                actions.push(LoadCurrentTenantPriceList({ payload: undefined }));
            }
            if (authState.currentTenantId && authState.authUser?.canManageSubTenants(authState.currentTenantId)) {
                const currentTenantProfile = authState.authUser.getTenantProfile(authState.currentTenantId);
                const isPrepaidTenant = currentTenantProfile?.billingType?.toLowerCase() === 'prepaid';

                if (tenantData.subTenantsStatus === STATUS_NOT_LOADED) {
                    actions.push(LoadCurrentTenantSubTenants({ payload: undefined }));
                }
                if (!isPrepaidTenant && tenantData.pricingPlansStatus === STATUS_NOT_LOADED) {
                    actions.push(LoadCurrentTenantPricingPlans({ payload: undefined }));
                }
            }
            if (versionsState.status === null) {
                actions.push(LoadAvailableNeoSimitVersions({ payload: undefined }));
            }
            return actions;
        })
    ))

    refreshCurrentTenantProjects$ = createEffect(() => this.actions.pipe(
        ofType(RefreshCurrentTenantProjects),
        withLatestFrom(this.store.pipe(select(authPortalUserState))),
        filter(([_, authState]) => authState.status === 'loaded' && authState.authUser !== null && authState.currentTenantId !== null),
        mergeMap(([_, authState]) => {
            if (authState.currentTenantId) {
                return this.tenantProjectService.getTenantProjects(authState.currentTenantId).pipe(
                    map(projects => SuccessCurrentTenantProjects({ payload: projects })),
                    catchError(() => of(FailureCurrentTenantProjects({ payload: undefined })))
                );
            } else {
                return of(FailureCurrentTenantProjects({ payload: undefined }));
            }
        })
    ));

    loadCurrentTenantProjects$ = createEffect(() => this.actions.pipe(
        ofType(LoadCurrentTenantProjects),
        switchMap(() => interval(60000).pipe(
            startWith(0), // Start immediately without waiting for the first 60 seconds to elapse
            withLatestFrom(this.store.pipe(select(authPortalUserState))),
            filter(([_, authPortalUserState]) => { return authPortalUserState.status === 'loaded' && authPortalUserState.authUser !== null && authPortalUserState.currentTenantId !== null }),
            switchMap(([_, authPortalUserState]) => {

                if (authPortalUserState.currentTenantId) {
                    return this.tenantProjectService.getTenantProjects(authPortalUserState.currentTenantId).pipe(
                        map(projects => SuccessCurrentTenantProjects({ payload: projects })),
                        catchError((error) => of(FailureCurrentTenantProjects({ payload: undefined })))
                    );
                } else {
                    return of(FailureCurrentTenantProjects({ payload: undefined }));
                }
            })
        )),
    ));

    loadAvailableNeoSimitVersions$ = createEffect(() => this.actions.pipe(
        ofType(LoadAvailableNeoSimitVersions),
        withLatestFrom(this.store.select(selectAvailableNeoSimitVersionsState)),
        filter(([actions, state]) => state.status !== 'loaded'),
        mergeMap(([actions]) => {
            return this.parameterStore.getAvailableNeoSimitVersions().pipe(
                map(params => { return SuccessAvailableNeoSimitVersions({ payload: params.map(p => p.trim()) }) }),
                catchError((error: any) => of(FailAvailableNeoSimitVersions({ payload: undefined })))
            )

        })
    ))

    loadCurrentTenantUsers$ = createEffect(() => this.actions.pipe(
        ofType(LoadCurrentTenantUsers),
        withLatestFrom(this.store.pipe(select(authPortalUserState))),
        filter(([_, authPortalUserState]) => authPortalUserState.status === 'loaded' && authPortalUserState.authUser !== null && authPortalUserState.currentTenantId !== null),
        switchMap(([_, authPortalUserState]) => {
            if (authPortalUserState.currentTenantId) {
                return this.tenantUserService.getTenantUsers(authPortalUserState.currentTenantId).pipe(
                    map(users => SuccessCurrentTenantUsers({ payload: users })),
                    catchError((error) => of(FailureCurrentTenantUsers({ payload: undefined })))
                );
            } else {
                return of(FailureCurrentTenantUsers({ payload: undefined }));
            }
        })
    ));

    loadCurrentTenantPriceList$ = createEffect(() => this.actions.pipe(
        ofType(LoadCurrentTenantPriceList),
        withLatestFrom(this.store.pipe(select(authPortalUserState))),
        filter(([_, authState]) => authState.status === 'loaded' && authState.authUser !== null && authState.currentTenantId !== null),
        switchMap(([_, authState]) => {
            if (authState.currentTenantId) {
                return this.priceListService.getTenantPriceList(authState.currentTenantId).pipe(
                    map(priceList => SuccessCurrentTenantPriceList({ payload: priceList })),
                    catchError(() => of(FailureCurrentTenantPriceList({ payload: undefined })))
                );
            } else {
                return of(FailureCurrentTenantPriceList({ payload: undefined }));
            }
        })
    ));

    loadCurrentTenantSubTenants$ = createEffect(() => this.actions.pipe(
        ofType(LoadCurrentTenantSubTenants),
        withLatestFrom(this.store.pipe(select(authPortalUserState))),
        filter(([_, authPortalUserState]) => authPortalUserState.status === 'loaded' && authPortalUserState.authUser !== null && authPortalUserState.currentTenantId !== null),
        switchMap(([_, authPortalUserState]) => {
            if (authPortalUserState.currentTenantId) {
                return this.subTenantService.getSubTenants(authPortalUserState.currentTenantId).pipe(
                    map(subTenants => SuccessCurrentTenantSubTenants({ payload: subTenants })),
                    catchError((error) => of(FailureCurrentTenantSubTenants({ payload: undefined }))
                    )
                );
            } else {
                return of(FailureCurrentTenantSubTenants({ payload: undefined }));
            }
        })
    ));

    loadCurrentPricingPlans$ = createEffect(() => this.actions.pipe(
        ofType(LoadCurrentTenantPricingPlans),
        withLatestFrom(this.store.pipe(select(authPortalUserState))),
        filter(([_, authPortalUserState]) => authPortalUserState.status === 'loaded' && authPortalUserState.authUser !== null && authPortalUserState.currentTenantId !== null),
        switchMap(([_, authPortalUserState]) => {
            if (authPortalUserState.currentTenantId) {
                const currentTenantProfile = authPortalUserState.authUser!.getTenantProfile(authPortalUserState.currentTenantId);
                const isPrepaidTenant = currentTenantProfile?.billingType?.toLowerCase() === 'prepaid';
                if (isPrepaidTenant) {
                    return of(SuccessCurrentTenantPricingPlans({ payload: [] }));
                }

                return this.pricingPlanService.getPricingPlans(authPortalUserState.currentTenantId).pipe(
                    map(plans => SuccessCurrentTenantPricingPlans({ payload: plans })),
                    catchError((error) => of(FailureCurrentTenantPricingPlans({ payload: undefined }))
                    )
                );
            } else {
                return of(FailureCurrentTenantPricingPlans({ payload: undefined }));
            }
        })
    ));

    loadAnnouncements$ = createEffect(() => this.actions.pipe(
        ofType(LoadAnnouncements),
        withLatestFrom(this.store.pipe(select(authPortalUserState))),
        filter(([_, authPortalUserState]) => authPortalUserState.status === 'loaded' && authPortalUserState.authUser !== null && authPortalUserState.currentTenantId !== null),
        switchMap(([_, authPortalUserState]) => {
            if (authPortalUserState.currentTenantId) {
                return this.usersManagementService.getAnnouncements().pipe(
                    map(announcements => SuccessAnnouncements({ payload: announcements })),
                    catchError((error) => of(FailureAnnouncements({ payload: undefined })))
                );
            } else {
                return of(FailureAnnouncements({ payload: undefined }));
            }
        }
        )));


    loadAnnouncementsImages$ = createEffect(() => this.actions.pipe(
        ofType(SuccessAnnouncements),
        withLatestFrom(this.store.pipe(select(announcementState))),
        filter(([_, announcementState]) => announcementState.status === 'loaded' && announcementState.announcements.length > 0),
        mergeMap(([_, announcementState]) => {
            return from(Object.keys(announcementState.images)).pipe(
                mergeMap(imageKey =>
                    this.usersManagementService.getAnnouncementImage(imageKey).pipe(
                        map((value) => SuccessSingleAnnouncementImage({ payload: { imageKey: imageKey, value: value } })),
                        catchError((error) => { return of(FailureSingleAnnouncementImage({ payload: { imageKey: imageKey } })) })
                    )
                )
            )
        })
    ));

    constructor(
        private readonly actions: Actions,
        private readonly usersManagementService: UserManagementService,
        private readonly store: Store,
        private readonly parameterStore: ParamterStoreService,
        private readonly authService: AuthService,
        private readonly tenantProjectService: TenantProjectService,
        private readonly tenantUserService: TenantUserService,
        private readonly subTenantService: SubTenantService,
        private readonly pricingPlanService: PricingplanService,
        private readonly priceListService: PriceListService,
    ) {
    }
}